// Espace employé — unified request rows: leave requests, worked-day
// claims and document requests in one chronological list with an
// expandable status timeline (design EsReqRow).
import type { MeClaimDTO, MeDocRequestDTO, MeLeaveRequestDTO, MeRequests } from '../../api/hooks.js';
import { CG_MONTHS, cgFmtLong, cgFmtRange } from '../conges/common.js';
import {
  CLAIM_ST, DOC_SERVICE, DOC_SLA, DOC_ST, ES_SERVICES, ES_TONE, EsTrack, LEAVE_ST, LEAVE_TYPE_LBL,
} from './common.js';

export type EsItem =
  | { k: 'l'; id: string; date: string; live: boolean; leave: MeLeaveRequestDTO }
  | { k: 'c'; id: string; date: string; live: boolean; claim: MeClaimDTO }
  | { k: 'd'; id: string; date: string; live: boolean; doc: MeDocRequestDTO };

export const OPEN_DOC = new Set(['RECUE', 'PREPARATION', 'SIGNATURE']);

export function buildItems(data: MeRequests | undefined): EsItem[] {
  if (!data) return [];
  const items: EsItem[] = [
    ...data.leaves.map((l): EsItem => ({ k: 'l', id: l.id, date: l.createdAt, live: l.stage === 'PROPOSED_BY_LAMIS', leave: l })),
    ...data.claims.map((c): EsItem => ({ k: 'c', id: c.id, date: c.createdAt, live: c.stage === 'PENDING', claim: c })),
    ...data.docs.map((d): EsItem => ({ k: 'd', id: d.id, date: d.createdAt, live: OPEN_DOC.has(d.status), doc: d })),
  ];
  return items.sort((a, b) => (a.date < b.date ? 1 : -1));
}

const monthsLbl = (months: string[]) => months.map((k) => CG_MONTHS[Number(k.slice(-2)) - 1]).join(', ');

export function EsReqRow({ it, open, onToggle, onCancel }: {
  it: EsItem;
  open: boolean;
  onToggle: () => void;
  onCancel: (it: EsItem) => void;
}) {
  if (it.k === 'd') {
    const d = it.doc;
    const svc = ES_SERVICES[DOC_SERVICE[d.type]];
    const [bg, fg] = ES_TONE[svc.tone];
    const [lbl, cls] = DOC_ST[d.status];
    return (
      <div className={'es-item' + (open ? ' open' : '') + (it.live ? ' attente' : '') + (d.status === 'REFUSEE' ? ' refuse' : '')} onClick={onToggle}>
        <span className="es-item-ic" style={{ background: bg, color: fg }}>{svc.ic}</span>
        <div className="es-item-m">
          <b>
            {svc.lbl}
            {d.months.length > 0 && ` · ${monthsLbl(d.months)}`}
            {d.leaveStart && d.leaveEnd && ` · ${cgFmtRange(d.leaveStart, d.leaveEnd)}`}
          </b>
          <i>
            {d.forme === 'ORIGINAL' ? 'Original signé par le DG' : 'Copie électronique'} · {d.copies} exemplaire{d.copies > 1 ? 's' : ''}
            {d.dest ? ` · ${d.dest}` : ''} · demandé le {cgFmtLong(d.createdAt.slice(0, 10))}
          </i>
          {open && (
            <div className="es-item-tl" onClick={(e) => e.stopPropagation()}>
              {d.status !== 'REFUSEE' && <EsTrack status={d.status} forme={d.forme} />}
              {d.status === 'REFUSEE' && <span><i className="dot ko" />Demande refusée par la RH</span>}
              {d.note && <span className="es-note">« {d.note} »</span>}
              <span className="es-item-sla">Délai annoncé : {DOC_SLA[d.forme]} · traitée par la RH</span>
              {d.status === 'RECUE' && <button className="es-cancel" onClick={() => onCancel(it)}>Annuler la demande</button>}
            </div>
          )}
        </div>
        <span className={'es-chip ' + cls}>{lbl}</span>
        <span className="es-item-caret">{open ? '▴' : '▾'}</span>
      </div>
    );
  }

  if (it.k === 'c') {
    const c = it.claim;
    const [lbl, cls] = CLAIM_ST[c.stage];
    return (
      <div className={'es-item' + (open ? ' open' : '') + (it.live ? ' attente' : '') + (c.stage === 'REJECTED' ? ' refuse' : '')} onClick={onToggle}>
        <span className="es-item-ic" style={{ background: 'var(--warns)', color: 'var(--warnc)' }}>＋</span>
        <div className="es-item-m">
          <b>Journée travaillée · {cgFmtLong(c.date)}</b>
          <i>{c.motif ?? 'Week-end ou férié travaillé'} · +{String(c.amount).replace('.', ',')} j de récup</i>
          {open && (
            <div className="es-item-tl" onClick={(e) => e.stopPropagation()}>
              <span><i className="dot done" />Déclarée le {cgFmtLong(c.createdAt.slice(0, 10))}</span>
              <span>
                <i className={'dot ' + (c.stage === 'PENDING' ? 'wait' : c.stage === 'APPROVED' ? 'done' : 'ko')} />
                {c.stage === 'PENDING' ? 'En attente de validation par votre responsable'
                  : c.stage === 'APPROVED' ? `Créditée le ${c.decidedAt ? cgFmtLong(c.decidedAt.slice(0, 10)) : ''} · +${String(c.amount).replace('.', ',')} j`
                  : `Refusée${c.rejectionReason ? ` — ${c.rejectionReason}` : ''}`}
              </span>
              {c.stage === 'PENDING' && <button className="es-cancel" onClick={() => onCancel(it)}>Annuler la déclaration</button>}
            </div>
          )}
        </div>
        <span className={'es-chip ' + cls}>{lbl}</span>
        <span className="es-item-caret">{open ? '▴' : '▾'}</span>
      </div>
    );
  }

  const l = it.leave;
  const T = LEAVE_TYPE_LBL[l.type];
  const [lbl, cls] = LEAVE_ST[l.stage];
  return (
    <div className={'es-item' + (open ? ' open' : '') + (it.live ? ' attente' : '') + (l.stage === 'REJECTED' ? ' refuse' : '')} onClick={onToggle}>
      <span className="es-item-ic" style={{ background: T.bg, color: T.c }}>{l.type === 'RECUP' ? '↧' : '✈'}</span>
      <div className="es-item-m">
        <b>{T.lbl} · {cgFmtRange(l.startDate, l.endDate)}</b>
        <i>{l.daysCount} jour{l.daysCount > 1 ? 's' : ''} ouvrable{l.daysCount > 1 ? 's' : ''}{l.reason ? ` — « ${l.reason} »` : ''}</i>
        {open && (
          <div className="es-item-tl" onClick={(e) => e.stopPropagation()}>
            <span><i className="dot done" />Demande envoyée le {cgFmtLong(l.createdAt.slice(0, 10))}</span>
            <span>
              <i className={'dot ' + (l.stage === 'PROPOSED_BY_LAMIS' ? 'wait' : l.stage === 'REJECTED' ? 'ko' : 'done')} />
              {l.stage === 'PROPOSED_BY_LAMIS' ? 'En attente de validation par votre responsable'
                : l.stage === 'REJECTED' ? `Refusée${l.rejectionReason ? ` — ${l.rejectionReason}` : ''}`
                : `Validée${l.decidedAt ? ` le ${cgFmtLong(l.decidedAt.slice(0, 10))}` : ''}`}
            </span>
            {(l.stage === 'APPROVED' || l.stage === 'DEBITED') && <span><i className="dot done" />Solde débité de {l.daysCount} j</span>}
            {l.stage === 'PROPOSED_BY_LAMIS' && <button className="es-cancel" onClick={() => onCancel(it)}>Annuler la demande</button>}
          </div>
        )}
      </div>
      <span className={'es-chip ' + cls}>{lbl}</span>
      <span className="es-item-caret">{open ? '▴' : '▾'}</span>
    </div>
  );
}
